// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machine, in its own chunk (S7-09, R4).
//
// WHAT THIS IS FOR
// ----------------
// `main.tsx` imports `Landing` and `PlayRoute` statically, because they are the front door and the
// address every share link points at. `Landing` imports `MachineStage`, which imports
// `machineRuntime`, which reaches `src/world/*` — and those import `three`. So **Three.js was in
// the entry graph of every route**, and the privacy policy downloaded a 615 KB 3-D renderer it has
// no use for. Measured 2026-09-06: 1,867,107 bytes of JavaScript preloaded on all twelve pages,
// identical on `/privacy` and on `/`, which is the mechanical cause of the crawl profile the
// Search Console read found — 63% of Googlebot's requests were JavaScript against 17% HTML.
//
// This is the same mechanism `LAZY_ROUTES` already applies to the Builder, for the reason
// `routes.ts` states there: *"a canvas, its painting and its editing model have no business in the
// bundle that draws the front door."* The renderer has no business in the bundle that draws the
// privacy policy, and the argument is the same one pointed the other way.
//
// **AND IT MUST NOT COST THE FRONT DOOR ANYTHING**
// -------------------------------------------------
// 08 §2 requires instant NEON NOVA play, and a lazy chunk is normally a waterfall: the browser has
// to fetch `main`, execute it, discover the dynamic import, and only then ask for the renderer.
// That would be a real regression and it is why this was left open rather than done.
//
// The escape is that this product **prerenders one HTML file per route**. The build knows which
// chunks this import pulls in, and `vite.config.ts` writes `<link rel="modulepreload">` for them
// into the three pages that run a machine — `/` and both `/play/{machine}` — and into no others.
// So the fetch still starts in parallel with `main` exactly as it does today, and a document route
// never asks for it at all. Measured either side of the change: the front door's time to a
// pressable plunger is unchanged, and `/privacy` stops fetching four of its seven modules.
//
// **The preload is load-bearing, not an optimisation.** If it were ever dropped, the front door
// would silently become a waterfall — the page would still work, so nothing would fail except the
// promise. `test/seo/machineChunks.test.ts` asserts the tags are on exactly the machine routes and
// that they name chunks the build really wrote.

import { Suspense, lazy } from 'react'
import type { MachineStageProps } from './MachineStage'

/**
 * The one dynamic import of the renderer.
 *
 * **In one place on purpose.** `Landing` and `PlayRoute` both draw a machine, and two `lazy()`
 * calls over the same module would be two Suspense boundaries with two fallbacks to keep in step —
 * and, more to the point, the build's chunk lookup would have two facades to find rather than one.
 * The prerenderer's preload list is computed from this module's chunk, so there being exactly one
 * of it is what makes that lookup a single unambiguous answer.
 */
const MachineStage = lazy(async () => ({
  default: (await import('./MachineStage')).MachineStage,
}))

/**
 * The cabinet silhouette both routes already show while a machine is on its way.
 *
 * Lifted out of `PlayRoute` and `Landing`, which drew it identically for their own waits — a
 * definition read, and a browser deciding which machine is active. This is a third wait of the same
 * kind and it should look like the other two rather than like a new thing happening.
 *
 * `PlayRoute`'s comment is the reason it is a shape and not a spinner: *"A spinner says something
 * is happening; a shape says what is coming, and it occupies the room the machine will take so
 * nothing jumps."* And `.skeleton` is absolutely positioned, so it needs `page__skeleton` round it
 * or it fills the shell instead of the pane.
 */
export function CabinetSkeleton(): React.JSX.Element {
  return (
    <div className="page__skeleton">
      <div className="skeleton" aria-hidden="true">
        <div className="skeleton__cabinet" />
      </div>
    </div>
  )
}

/**
 * `MachineStage`, fetched on demand and preloaded where it is needed.
 *
 * Takes and forwards `MachineStageProps` unchanged, so a call site reads exactly as it did when the
 * import was static — which matters, because the two call sites are the two most load-bearing
 * routes in the product and a change here should not also be a change to how they are written.
 */
export function LazyMachineStage(props: MachineStageProps): React.JSX.Element {
  return (
    <Suspense fallback={<CabinetSkeleton />}>
      <MachineStage {...props} />
    </Suspense>
  )
}
